import apiService from './ApiService';
import extensionService from '@/services/ExtensionService';
import { User } from '@/models/User';
import { Company } from '@/models/Company';
import { AnyJson } from '@/@types/AnyJson';
import { CompanyUserStatus } from '@/@types/enums/CompanyUserStatus';
import { CompanyRole } from '@/@types/CompanyRole';
import { Logger } from '@/other/Logger';
import { Mutations } from '@/store/mutations';
import { Store } from 'vuex';
import { CompanyResponse } from '@/@types/responses/CompanyResponses';
import { UserResponse } from '@/@types/responses/UserResponses';
import localStorageService from '@/services/LocalStorageService';
import { clearAppContext } from '@/helpers/appContext';

const log = new Logger('AuthService');

export class AuthService {
  protected store: Store<any>;

  setStore(store: Store<any>) {
    this.store = store;
  }

  get user(): User {
    return this.store.state.user;
  }

  setUser(data: UserResponse): User {
    const user = User.parse(data);
    this.store.commit(Mutations.SET_USER, user);
    return user;
  }

  setCompany(data: CompanyResponse) {
    const company = Company.parse(data);
    this.store.commit(Mutations.SET_COMPANY, company);
    this.store.commit('setCompanyLoading', false);
    extensionService.logInUser();
  }

  isGuest(): boolean {
    return !(this.user && this.user.email);
  }

  /** The session is an HttpOnly cookie this code cannot read, so "authorized" means a user was loaded from the API. */
  isAuthorized(): boolean {
    return !!(this.user && this.user.id);
  }

  logout(): void {
    extensionService.logOutUser();
    apiService.clearLegacySession();
    this.store.commit(Mutations.SET_USER, null);
    this.store.commit(Mutations.SET_COMPANY, null);
    this.store.commit(Mutations.SET_USER_COMPANIES, null);
    localStorageService.clear();
    // H3: a stale guides context must not survive a logout, or the next
    // (unrelated) login on this tab gets hijacked into the Guides app.
    clearAppContext();
  }

  /**
   * SNAP-2389: clear local state AND leave the app.
   *
   * `logout()` on its own only forgets the token; it does not navigate. Callers that end the
   * session the user is currently holding (the header menu, and the Active Sessions page)
   * must also send them to the login screen, or they are left on a page that renders blank
   * and 401s on the next click. Kept as one method so that pairing cannot be forgotten.
   */
  logoutAndRedirect(): void {
    this.logout();
    document.location.href = `${process.env.VUE_APP_URL}/#/login`;
  }

  /**
   * User-initiated logout (SNAP-2298, SNAP-2309): revoke this device's session on the server, which
   * also clears the session cookie, then clear the local state. A 401 means the session is already
   * gone. Any other failure throws and leaves the user signed in here, because the HttpOnly session
   * may still be live and the UI must not claim otherwise. Forced logouts (401, deactivated user)
   * keep using `logout()`.
   */
  async signOut(): Promise<void> {
    try {
      await apiService.logoutUser('current');
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status ?? null;
      if (status !== 401) {
        log.error('Server-side logout failed, the session is still active', err);
        throw err;
      }
    }
    this.logout();
  }

  /**
   * Asks the API whether the browser's session cookie is still good. Never navigates: a 401 is the
   * normal answer for a signed-out visitor, so this reads the response instead of going through the
   * global 401 handler.
   */
  async probeSession(): Promise<boolean> {
    try {
      const res = await apiService.getUser();
      this.setUser(res.data.user);
      this.setCompany(res.data.user.company);
      this.store.commit(
        'setUserCompanies',
        res.data.companies.map((item: AnyJson) => Company.parse(item))
      );

      return true;
    } catch {
      return false;
    }
  }

  setUserCompanies(companies: Company[]) {
    this.store.commit('setUserCompanies', companies);
  }

  async isUserActive(): Promise<boolean> {
    try {
      const user = await this.loadUser();
      const company = user.company as Company;
      const roles: CompanyRole[] = Object.values(company.roles);
      if (company.user === user.id) {
        return true;
      }
      const activeUser: CompanyRole[] = roles.filter((item: CompanyRole) => {
        if (!item.status) return item;
        if (item.user === user.id && item.status !== CompanyUserStatus.DEACTIVATED) {
          return item;
        }
      });

      return !!activeUser.length;
    } catch (error) {
      log.error(error);
    }
  }

  async loadUser(): Promise<User> {
    try {
      const res = await apiService.getUser();
      const user = this.setUser(res.data.user);
      this.setCompany(res.data.user.company);
      this.store.commit(
        'setUserCompanies',
        res.data.companies.map((item: AnyJson) => Company.parse(item))
      );

      return user;
    } catch (err) {
      apiService.handleResponseError(err);
    }
  }
}

const authService = new AuthService();
export default authService;
